import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  InputAdornment,
  styled,
  Typography,
  CircularProgress,
  Grid,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import TextFormField from '@components/FormFields/TextFormField/TextFormField.component';
import { Form } from 'react-final-form';
import PlusIcon from 'icons/Plus.icon';
import EditIcon from 'icons/Edit.icon';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

export const Route = createFileRoute('/(dashboard)/_dashboard/profile/info')({
  component: RouteComponent,
});

const inputFields = [
  {
    name: 'firstName',
    label: 'First Name',
    type: 'text',
  },
  {
    name: 'lastName',
    label: 'Last Name',
    type: 'text',
  },
  {
    name: 'username',
    label: 'Username',
    type: 'text',
  },
  {
    name: 'company',
    label: 'Company',
    type: 'text',
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email',
  },
];

const StyledContainer = styled(Box)(() => ({
  // padding: '32px',
}));

function RouteComponent() {
  const queryClient = useQueryClient();

  const { data: personalSettings, isLoading } = useQuery({
    queryKey: ['personalSettings'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://interviewer.runasp.net/api/users/personal-settings', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (values: { firstName: string; lastName: string }) => {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        'https://interviewer.runasp.net/api/users/personal-settings',
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch personal settings after successful update
      queryClient.invalidateQueries({ queryKey: ['personalSettings'] });
      toast.success('Personal data changed successfully!')
    },
  });

  const handleSubmit = (values: any) => {
    updateSettingsMutation.mutate({
      firstName: values.firstName,
      lastName: values.lastName,
    });
  };

  return (
    <StyledContainer>
      <Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: 'var(--Interviewer-Black)',
            marginBottom: '16px',
          }}
        >
          Profile picture
        </Typography>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <IconButton
              sx={{
                background: 'var(--Interviewer-Black)',
                border: '3px solid #FFFFFF',

                '&:hover': {
                  background: 'var(--Interviewer-Black)',
                },
              }}
            >
              <PlusIcon />
            </IconButton>
          }
        >
          <Avatar
            alt="Danilo Mladenovic"
            sx={{ height: {xs: '64px', md: '128px'}, width: {xs: '64px', md: '128px'} }}
          />
        </Badge>
      </Box>
      <Form
        onSubmit={handleSubmit}
        initialValues={personalSettings}
        render={({ handleSubmit }) => (
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              marginTop: '48px',
              gap: '48px',
              flex: 1,
            }}
          >
            <Grid container spacing={4}>
              {inputFields.map(({ name, label, type }, index) => (
                <Grid
                  key={name}
                  size={{
                    xs: 12,
                    md: index === inputFields.length - 1 ? 12 : 6,
                  }}
                >
                  {/* <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: 'var(--Interviewer-Black)',
                      marginBottom: '16px',
                    }}
                  >
                    {label}
                  </Typography> */}
                  <TextFormField
                    name={name}
                    label={label}
                    type={type}
                    disabled={isLoading || updateSettingsMutation.isPending}
                    slotProps={{
                      input: {
                        endAdornment: index > 1 && (
                          <InputAdornment position="end">
                            <EditIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            {updateSettingsMutation.error && (
              <Typography color='error' variant='body2'>
                {updateSettingsMutation.error.message || 'Error message'}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: '16px',
                alignSelf: { xs: 'stretch', sm: 'flex-end' },
                flexDirection: { xs: 'column', sm: 'row' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button 
                type="submit" 
                variant="contained"
                disabled={updateSettingsMutation.isPending}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 1, sm: 2 },
                }}
              >
                {updateSettingsMutation.isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Save Changes'
                )}
              </Button>
              <Button
                type="reset"
                variant="outlined"
                sx={{
                  border: '1px solid #FFFFFF1A',
                  background: 'var(--Light-Gray)',
                  color: 'var(--Interviewer-Black)',
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 2, sm: 1 },
                }}
                disabled={updateSettingsMutation.isPending}
              >
                Cancel
              </Button>
            </Box>
          </form>
        )}
      />
    </StyledContainer>
  );
}
